import * as Linking from 'expo-linking';
import * as Notifications from 'expo-notifications';
import {
  PermissionStatus,
  getTrackingPermissionsAsync,
  requestTrackingPermissionsAsync,
} from 'expo-tracking-transparency';
import { Platform } from 'react-native';

import { ILocation } from '@rbi-ctg/frontend';
import { updateAmplitudeATTStatus } from 'state/amplitude/amplitude-att-plugin';
import {
  GeolocationPermissionStates,
  checkIfLocationPermissionIsPreapproved,
  getUsersCurrentPosition,
  promptUserForLocationPermission,
} from 'utils/geolocation';
import logger from 'utils/logger';

export const enablePush = async (): Promise<Notifications.NotificationPermissionsStatus | null> => {
  try {
    const settings = await Notifications.getPermissionsAsync();
    if (
      settings.status === PermissionStatus.UNDETERMINED ||
      settings.ios?.status === Notifications.IosAuthorizationStatus.NOT_DETERMINED
    ) {
      return await Notifications.requestPermissionsAsync();
    }
    return settings;
  } catch (error) {
    logger.error({ error, message: 'Prompt For Push Permissions error' });
    return null;
  }
};

export const openPushSetting = () => {
  if (Platform.OS === 'android') {
    Linking.openSettings();
  } else if (Platform.OS === 'ios') {
    Linking.openURL('app-settings:');
  }
};

class LocationPermissionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LocationPermissionError';
  }
}

/**
 * enable location services and retrieve the user's current location.
 * It checks the current state of location permissions and either directly retrieves the location
 * or prompts the user for permission first.
 *
 * @returns A Promise resolving to the user's location if permissions are granted, or undefined otherwise.
 * Throws a LocationPermissionError if permission is explicitly denied by the user.
 */
export const enableLocation = async (): Promise<ILocation | void> => {
  try {
    const state = await checkIfLocationPermissionIsPreapproved();

    if (state === GeolocationPermissionStates.GRANTED) {
      return getUsersCurrentPosition();
    }

    if (state === GeolocationPermissionStates.PROMPT) {
      const permissionState = await promptUserForLocationPermission();

      if (permissionState === GeolocationPermissionStates.DENIED) {
        throw new LocationPermissionError('User denied location permission');
      }

      return getUsersCurrentPosition();
    }
  } catch (error) {
    logger.error({
      error,
      message: 'Error while enabling location',
    });
  }
};

export const appTrackingTransparency = async (): Promise<PermissionStatus | undefined> => {
  try {
    const { granted, canAskAgain, status } = await getTrackingPermissionsAsync();

    if (!granted && canAskAgain) {
      const response = await requestTrackingPermissionsAsync();

      // Update Amplitude att status after we have requested permissions
      updateAmplitudeATTStatus(response.status);

      return response.status;
    }

    return status;
  } catch (error) {
    logger.error({
      error,
      message: 'Error in fetching user App Tracking Transparency status',
      // In legacy implementations, this was always set to true. I'm keeping this here hardcoded
      // just to ensure our data lines up as expected. But it seems like this could go away without
      // any consequences.
      requestPermission: true,
    });
  }
  return undefined;
};
